import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import api from '../lib/api';
import { Link } from '@/lib/router-components';

const PROCESSES_LIMIT = 5;

export const Route = createFileRoute('/client/')({
  component: ClientHomePage,
});

function formatDateTime(date: Date | string | null | undefined) {
  if (!date) return '';
  return new Date(date).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function ClientHomePage() {
  const [processPage, setProcessPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setProcessPage(1);
  }, [debouncedSearch]);

  const { data: processesPayload, isLoading: isLoadingProcesses } = useQuery({
    queryKey: ['client-portal', 'my-processes', processPage, debouncedSearch],
    queryFn: async () => {
      const r = await api.get('/client-portal/my-processes', {
        params: {
          page: processPage,
          limit: PROCESSES_LIMIT,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
        },
      });
      return (r.data?.data ?? r.data) as {
        data: any[];
        total: number;
        page: number;
        limit: number;
        totalPages: number;
      };
    },
  });

  const { data: feedPayload, isLoading: isLoadingFeed } = useQuery({
    queryKey: ['client-portal', 'my-feed'],
    queryFn: async () => {
      const r = await api.get('/client-portal/my-feed');
      return (r.data?.data ?? r.data) as { data: any[] };
    },
  });

  const processes = processesPayload?.data ?? [];
  const processTotal = processesPayload?.total ?? 0;
  const processTotalPages = processesPayload?.totalPages ?? 0;

  const feed = feedPayload?.data ?? [];

  return (
    <div className="p-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="bg-white rounded-[15px] p-5 shadow-sm border border-gray-100">
          <h1 className="text-[18px] font-semibold text-[#06141B]">Início</h1>
          <p className="text-[12px] text-gray-500 mt-1">
            Aqui você encontra os processos compartilhados com você e o feed de atualizações.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <section className="lg:col-span-2 bg-white rounded-[15px] p-5 shadow-sm border border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <h2 className="text-[14px] font-semibold text-[#06141B]">Processos compartilhados</h2>
              <span className="text-xs text-gray-500">
                {processTotal} {processTotal === 1 ? 'processo' : 'processos'}
              </span>
            </div>

            <div className="mt-3">
              <label className="text-[11px] font-medium text-gray-600">Filtrar</label>
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Número, título ou partes..."
                className="mt-1 w-full px-3 py-2 rounded-[10px] border border-gray-200 text-sm bg-gray-50"
              />
            </div>

            {isLoadingProcesses ? (
              <p className="text-sm text-gray-500 mt-4">Carregando processos...</p>
            ) : !processes.length ? (
              <p className="text-sm text-gray-500 mt-4">
                Nenhum processo compartilhado ainda.
              </p>
            ) : (
              <div className="mt-4 space-y-2">
                {processes.map((p) => (
                  <Link
                    key={p.id}
                    to="/client/processes/$processId"
                    params={{ processId: p.id }}
                    className="block p-4 rounded-[12px] border border-gray-100 hover:border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-[#06141B] truncate">
                          {p.internalTitle || p.parties || 'Processo'}
                        </p>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <p className="text-xs text-gray-600">{p.processNumber}</p>
                          {Array.isArray(p.tags) && p.tags.length > 0 && p.tags.map((tag: any) => (
                            <span
                              key={tag.id}
                              className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700"
                            >
                              {tag.name}
                            </span>
                          ))}
                        </div>
                        {Array.isArray(p.parts) && p.parts.length > 0 ? (
                          <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
                            {p.parts.map((part: any) => (
                              <span key={part.id} className="text-[11px] text-gray-500">
                                {part.name}
                                {part.pole ? (
                                  <span className="text-[10px] text-gray-400 ml-1">({part.pole})</span>
                                ) : null}
                              </span>
                            ))}
                          </div>
                        ) : p.parties && p.internalTitle ? (
                          <p className="text-[11px] text-gray-500 mt-0.5 truncate">{p.parties}</p>
                        ) : null}
                      </div>
                      <span className="text-xs px-2.5 py-1 rounded-[10px] bg-gray-100 text-gray-700 whitespace-nowrap shrink-0">
                        {p.status}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {processTotalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => setProcessPage((p) => Math.max(1, p - 1))}
                  disabled={processPage === 1}
                  className="px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium bg-[#F9FAFB] text-[#2D3748] border border-gray-200"
                >
                  &lt;
                </button>
                <span className="px-4 py-2 text-xs text-gray-700">
                  Página {processPage} de {processTotalPages} ({processTotal}{' '}
                  {processTotal === 1 ? 'processo' : 'processos'})
                </span>
                <button
                  type="button"
                  onClick={() => setProcessPage((p) => Math.min(processTotalPages, p + 1))}
                  disabled={processPage === processTotalPages}
                  className="px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium bg-[#F9FAFB] text-[#2D3748] border border-gray-200"
                >
                  &gt;
                </button>
              </div>
            )}
          </section>

          <section className="bg-white rounded-[15px] p-5 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <h2 className="text-[14px] font-semibold text-[#06141B]">Feed</h2>
              <span className="text-xs text-gray-500">Últimas tarefas concluídas</span>
            </div>

            {isLoadingFeed ? (
              <p className="text-sm text-gray-500 mt-4">Carregando feed...</p>
            ) : !feed.length ? (
              <p className="text-sm text-gray-500 mt-4">Sem tarefas concluídas recentes.</p>
            ) : (
              <div className="mt-4 space-y-3">
                {feed.map((t: any) => (
                  <div key={t.id} className="p-3 rounded-[12px] bg-gray-50 border border-gray-100">
                    <p className="text-xs text-gray-500">
                      {t.process?.processNumber || 'Processo'}{' '}
                      {t.completedAt ? `• ${formatDateTime(t.completedAt)}` : ''}
                    </p>
                    <p className="text-sm text-[#06141B] mt-1 font-medium truncate">{t.title}</p>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
